import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLASS = {
  NORMAL: "sw-pill-normal",
  WATCH: "sw-pill-watch",
  REVIEW: "sw-pill-review",
  HIGH_PRIORITY: "sw-pill-high",
};

const useSignalTester = () => {
  const [form, setForm] = useState({
    supplier_name: "Acme Ltd",
    supplier_id: "sup-acme",
    category: "IT Hardware",
    amount: "150000",
    currency: "USD",
    transaction_date: "2026-06-15",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.supplier_id.trim() || !form.amount || !form.currency.trim() || !form.transaction_date) {
      setErr("Supplier ID, amount, currency and transaction date are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/v1/signal`, {
        supplier_name: form.supplier_name.trim() || null,
        supplier_id: form.supplier_id.trim(),
        category: form.category.trim() || null,
        amount: Number(form.amount),
        currency: form.currency.trim(),
        transaction_date: form.transaction_date,
      });
      setResult(res.data);
    } catch (error) {
      console.error("SignalTester: submit failed:", error);
      setErr("Couldn't compute a signal. Please check the fields and try again.");
    } finally {
      setLoading(false);
    }
  };

  return { form, set, result, loading, err, submit };
};

const Field = ({ label, testid, ...props }) => (
  <label className="sw-field">
    <span className="sw-label">{label}</span>
    <input className="sw-input" data-testid={testid} {...props} />
  </label>
);

const TesterResult = ({ result, idPrefix }) => (
  <div className="sw-tester-result" data-testid={`${idPrefix}-result`}>
    <div className="sw-tester-result-head">
      <div>
        <div className="sw-sample-label">Priority score</div>
        <div className="sw-tester-score">
          {result.priority_score}<span className="sw-slash">/100</span>
        </div>
      </div>
      <span className={`sw-pill ${STATUS_CLASS[result.priority_status] || "sw-pill-watch"}`}
        data-testid={`${idPrefix}-status`}>
        {String(result.priority_status).replace(/_/g, " ")}
      </span>
    </div>
    <div className="sw-tester-contribs" data-testid={`${idPrefix}-contributors`}>
      {(result.top_contributors || []).map((c) => (
        <div className="sw-demo-signal" key={c.signal_name}>
          <div className="sw-demo-signal-top">
            <span className="sw-demo-signal-name">{c.signal_name.replace(/_/g, " ")}</span>
            <span className="sw-pill sw-pill-watch">{c.score}</span>
          </div>
          <p className="sw-demo-signal-text">{c.explanation}</p>
        </div>
      ))}
    </div>
    {(result.skipped_signals || []).length > 0 ? (
      <div className="sw-tester-skipped" data-testid={`${idPrefix}-skipped`}>
        {result.skipped_signals.map((s) => (
          <div className="sw-skip-row" key={s.signal_name}>
            <span className="sw-demo-signal-name">{s.signal_name.replace(/_/g, " ")}</span>
            <span className="sw-skip-msg">Insufficient data to calculate this signal</span>
          </div>
        ))}
      </div>
    ) : null}
  </div>
);

export default function SignalTester({ idPrefix = "tester" }) {
  const { form, set, result, loading, err, submit } = useSignalTester();

  return (
    <div className="sw-tester" data-testid={`${idPrefix}-container`}>
      <form className="sw-tester-form" onSubmit={submit} data-testid={`${idPrefix}-form`} noValidate>
        <div className="sw-tester-grid">
          <Field label="Supplier name" testid={`${idPrefix}-supplier-name`}
            value={form.supplier_name} onChange={set("supplier_name")} placeholder="Acme Ltd" />
          <Field label="Supplier ID *" testid={`${idPrefix}-supplier-id`}
            value={form.supplier_id} onChange={set("supplier_id")} placeholder="sup-42" />
          <Field label="Category" testid={`${idPrefix}-category`}
            value={form.category} onChange={set("category")} placeholder="IT Hardware" />
          <Field label="Amount *" testid={`${idPrefix}-amount`} type="number" min="0"
            value={form.amount} onChange={set("amount")} placeholder="150000" />
          <Field label="Currency *" testid={`${idPrefix}-currency`} maxLength={3}
            value={form.currency} onChange={set("currency")} placeholder="USD" />
          <Field label="Transaction date *" testid={`${idPrefix}-date`} type="date"
            value={form.transaction_date} onChange={set("transaction_date")} />
        </div>
        {err ? <p className="sw-error" data-testid={`${idPrefix}-error`}>{err}</p> : null}
        <button type="submit" className="sw-btn sw-btn-primary" disabled={loading}
          data-testid={`${idPrefix}-submit`}>
          {loading ? "Computing…" : "Submit Event"}
        </button>
      </form>

      {result ? <TesterResult result={result} idPrefix={idPrefix} /> : null}
    </div>
  );
}
